import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';

export default function NodeDetailsPanel({ node, treeData, isOpen, onClose, isSuperAdmin, onActionComplete }) {
  const { token } = useAuth();
  const panelRef = useRef(null);
  const backdropRef = useRef(null);
  const firstFocusableRef = useRef(null);
  const [actionError, setActionError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [processing, setProcessing] = useState(false);
  const [reassignInput, setReassignInput] = useState('');
  const [showReassignForm, setShowReassignForm] = useState(false);

  const handleUnplace = async () => {
    if (!node) return;
    // Removing a node that has descendants is never allowed (migration
    // 214) -- doing so breaks their tree_closure connectivity to
    // everyone above the removed node, silently orphaning them (they
    // keep a tree_nodes row but become unreachable from the root). The
    // backend now refuses outright instead of offering a "force"
    // bypass; descendants must be re-placed or removed first.
    if (!window.confirm(`Remove ${node.affiliate_code} from this tree position?`)) return;

    setProcessing(true);
    setActionError('');
    setActionMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/tree/unplace-node`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ node_id: node.node_id }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to unplace node');
      }
      setActionMessage('Node removed.');
      onActionComplete?.();
      onClose();
    } catch (err) {
      setActionError(err.message || 'Failed to unplace node');
    } finally {
      setProcessing(false);
    }
  };

  const handleReassignSponsor = async () => {
    if (!node || !reassignInput.trim()) return;
    setProcessing(true);
    setActionError('');
    setActionMessage('');
    try {
      // Resolve the new sponsor's Wellis ID to a user_id first via the
      // existing validate-affiliate endpoint (no separate lookup route
      // exists for this yet).
      const validateRes = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/validate-affiliate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ affiliate_code: reassignInput.trim() }),
      });
      const validateData = await validateRes.json();
      if (!validateRes.ok || !validateData.success) {
        throw new Error(validateData.error || 'Wellis ID not found');
      }

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/tree/reassign-sponsor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: node.user_id, new_sponsor_id: validateData.affiliate_id }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to reassign sponsor');
      }

      const impactNote = data.affected_commission_count > 0
        ? ` ${data.affected_commission_count} already-released Direct Referral commission(s) from this member's own purchases may need review under the new chain.`
        : '';
      setActionMessage(`Sponsor reassigned.${impactNote}`);
      setShowReassignForm(false);
      setReassignInput('');
      onActionComplete?.();
    } catch (err) {
      setActionError(err.message || 'Failed to reassign sponsor');
    } finally {
      setProcessing(false);
    }
  };

  // Handle focus trap and keyboard events
  useEffect(() => {
    if (!isOpen) return;

    const panel = panelRef.current;
    const backdrop = backdropRef.current;
    if (!panel || !backdrop) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      // Focus trapping: cycle between first and last focusable elements
      if (e.key === 'Tab') {
        const focusables = panel.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) || [];
        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last?.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first?.focus();
          }
        }
      }
    };

    const handleBackdropClick = (e) => {
      onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    backdrop.addEventListener('click', handleBackdropClick);

    // Move focus into the panel
    const closeButton = panel.querySelector('button[aria-label="Close details panel"]');
    closeButton?.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      backdrop.removeEventListener('click', handleBackdropClick);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Content view when a node is selected
  if (!node) {
    return null;
  }

  const ancestors = treeData.getAncestors(node.node_id);
  const sponsor = ancestors.length > 0 ? treeData.getNode(ancestors[ancestors.length - 1]) : null;
  const children = treeData.getChildren(node.node_id);

  return (
    <>
      {/* Backdrop (fixed, overlay) */}
      <div ref={backdropRef} className="fixed inset-0 bg-black/20 z-30" />

      {/* Panel (fixed, slide-in from right) */}
      <div
        ref={panelRef}
        className="fixed right-0 top-0 bottom-0 w-96 bg-surface border-l border-outline flex flex-col z-40 shadow-lg"
      >
        {/* Header */}
        <div className="bg-primary text-on-primary p-4 flex items-center justify-between flex-shrink-0">
          <h3 className="text-title-md font-medium">Node Details</h3>
          <button
            ref={firstFocusableRef}
            onClick={onClose}
            aria-label="Close details panel"
            className="text-on-primary hover:opacity-80 text-lg leading-none"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* User Info */}
          <div>
            <h4 className="text-body-sm font-medium text-on-background mb-2">User</h4>
            <div className="space-y-1">
              <div className="text-body-sm text-on-background">
                {node.user_name || node.user_email}
              </div>
              <div className="text-body-xs text-on-background/60 font-mono">
                {node.user_email}
              </div>
              <div className="text-body-xs text-on-background/60 font-mono bg-background p-2 rounded mt-2">
                {node.affiliate_code}
              </div>
            </div>
          </div>

          {/* Position Info */}
          <div className="border-t border-outline pt-4">
            <h4 className="text-body-sm font-medium text-on-background mb-2">Position</h4>
            <div className="space-y-2 text-body-sm">
              <div className="flex justify-between">
                <span className="text-on-background/60">Leg:</span>
                <span className="font-medium text-primary">{node.leg_position}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-on-background/60">Level:</span>
                <span className="font-medium text-primary">{node.depth}</span>
              </div>
              {node.top_leg && (
                <div className="flex justify-between">
                  <span className="text-on-background/60">Top Leg:</span>
                  <span className="font-medium text-primary">{node.top_leg}</span>
                </div>
              )}
            </div>
          </div>

          {/* Leg Sales Volume (Left/Middle/Right) -- live, computed from
              confirmed package purchases under each of this node's 3
              immediate legs. Not the old Team Bonus v1 frozen snapshot;
              Team Bonus itself is unaffected (v2 doesn't use legs). */}
          <div className="border-t border-outline pt-4">
            <h4 className="text-body-sm font-medium text-on-background mb-2">Leg Sales Volume</h4>
            <div className="grid grid-cols-3 gap-2 text-body-sm">
              <div className="text-center p-2 bg-background rounded">
                <div className="text-on-background/60 text-[10px]">Left (L)</div>
                <div className="font-bold text-primary">{Number(node.leg1_volume || 0).toFixed(2)}</div>
              </div>
              <div className="text-center p-2 bg-background rounded">
                <div className="text-on-background/60 text-[10px]">Middle (M)</div>
                <div className="font-bold text-primary">{Number(node.leg2_volume || 0).toFixed(2)}</div>
              </div>
              <div className="text-center p-2 bg-background rounded">
                <div className="text-on-background/60 text-[10px]">Right (R)</div>
                <div className="font-bold text-primary">{Number(node.leg3_volume || 0).toFixed(2)}</div>
              </div>
            </div>
          </div>

          {/* Sponsor/Upline */}
          {sponsor && (
            <div className="border-t border-outline pt-4">
              <h4 className="text-body-sm font-medium text-on-background mb-2">Sponsor</h4>
              <div className="bg-background p-3 rounded text-body-sm space-y-1">
                <div className="text-on-background font-medium">
                  {sponsor.user_name || sponsor.user_email}
                </div>
                <div className="text-body-xs text-on-background/60 font-mono">
                  {sponsor.affiliate_code}
                </div>
              </div>
            </div>
          )}

          {/* Superadmin actions */}
          {isSuperAdmin && (
            <div className="border-t border-outline pt-4">
              <h4 className="text-body-sm font-medium text-on-background mb-2">Admin Actions</h4>

              {actionError && (
                <div className="text-body-xs mb-2 p-2 rounded" style={{ backgroundColor: '#2a0d0d', color: '#c94961' }}>
                  {actionError}
                </div>
              )}
              {actionMessage && (
                <div className="text-body-xs mb-2 p-2 rounded" style={{ backgroundColor: '#0d2818', color: '#c9a961' }}>
                  {actionMessage}
                </div>
              )}

              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => handleUnplace()}
                  disabled={processing}
                  className="w-full px-3 py-2 rounded text-body-xs font-medium border disabled:opacity-50"
                  style={{ backgroundColor: 'transparent', color: '#c94961', borderColor: '#c94961' }}
                >
                  {processing ? 'Working...' : 'Remove from tree'}
                </button>

                {!showReassignForm ? (
                  <button
                    type="button"
                    onClick={() => setShowReassignForm(true)}
                    disabled={processing}
                    className="w-full px-3 py-2 rounded text-body-xs font-medium border disabled:opacity-50"
                    style={{ backgroundColor: 'transparent', color: 'var(--on-background)', borderColor: 'var(--outline)' }}
                  >
                    Reassign sponsor
                  </button>
                ) : (
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="New sponsor's Wellis ID"
                      value={reassignInput}
                      onChange={(e) => setReassignInput(e.target.value)}
                      disabled={processing}
                      className="w-full px-2 py-2 rounded border text-body-xs"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleReassignSponsor}
                        disabled={processing || !reassignInput.trim()}
                        className="flex-1 px-3 py-2 rounded text-body-xs font-medium disabled:opacity-50"
                        style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
                      >
                        {processing ? 'Working...' : 'Confirm'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowReassignForm(false); setReassignInput(''); }}
                        disabled={processing}
                        className="flex-1 px-3 py-2 rounded text-body-xs font-medium border disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Downline Summary */}
          {children.length > 0 && (
            <div className="border-t border-outline pt-4">
              <h4 className="text-body-sm font-medium text-on-background mb-2">
                Downline ({children.length})
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {children.map((childId) => {
                  const child = treeData.getNode(childId);
                  return (
                    <div key={childId} className="bg-background p-2 rounded text-body-xs">
                      <div className="text-on-background font-medium truncate">
                        {child.user_name || child.user_email}
                      </div>
                      <div className="text-on-background/60 font-mono truncate">
                        {child.affiliate_code}
                      </div>
                      <div className="text-on-background/50 text-[11px]">
                        Leg {child.leg_position}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
